import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Button,
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Input,
  Textarea,
  RadioGroup,
  FormField,
} from '@/shared/components/ui';
import { useCreateAudience } from '../hooks';
import type { AudienceIdentifierType } from '@troia-v3/types';

export interface CreateAudienceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateAudienceModal: React.FC<CreateAudienceModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const createAudience = useCreateAudience();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [identifierType, setIdentifierType] = useState<AudienceIdentifierType>('phone');

  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setIdentifierType('phone');
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    try {
      const created = await createAudience.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        identifierType,
      });
      toast.success('Público criado com sucesso');
      onClose();
      navigate(`/prospecting/audiences/${created.id}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao criar público';
      toast.error(message);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="md">
      {/* F74: título 18px via prop tipada do header */}
      <ModalHeader title="Novo público" onClose={onClose} />
      <ModalContent>
        <div className="space-y-4">
          <FormField label="Nome" required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Clientes VIP"
              required
            />
          </FormField>
          <Textarea
            label="Descrição"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Descrição opcional"
            rows={3}
          />
          <RadioGroup
            label="Tipo de identificador"
            value={identifierType}
            onChange={(v) => setIdentifierType(v as AudienceIdentifierType)}
            options={[
              { value: 'phone', label: 'Telefone', description: 'Para campanhas de WhatsApp/SMS' },
              { value: 'email', label: 'Email', description: 'Para campanhas de email' },
            ]}
            variant="cards"
          />
        </div>
      </ModalContent>
      <ModalFooter>
        {/* F71: Cancelar = Button secondary (bordado) em todo footer de modal */}
        <Button
          type="button"
          variant="secondary"
          onClick={onClose}
          data-testid="create-audience-cancel"
        >
          Cancelar
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSubmit}
          disabled={!name.trim()}
          loading={createAudience.isPending}
          data-testid="create-audience-submit"
        >
          {createAudience.isPending ? 'Criando...' : 'Criar'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
